'use client'

import { useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from '@/i18n/client'
import { ExternalLink } from '@/components/legal/external-link'
import { AiLabel } from '@/components/ai/ai-label'

export function FglFooter() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const [fglWertungActive, setFglWertungActive] = useState(false)
  const [fglWertungVisibility, setFglWertungVisibility] = useState<'hidden' | 'tier_gated' | 'public'>('hidden')
  // 2026-05-20: Mobile-Accordion — Columns sind auf Mobile zugeklappt,
  // damit der Footer kompakt bleibt. Auf lg+ wirkungslos (CSS-override).
  const [openCols, setOpenCols] = useState<Record<string, boolean>>({})
  const toggleCol = (label: string) => setOpenCols(p => ({ ...p, [label]: !p[label] }))

  // R90.6 (2026-05-18): Tier-Gating + Wertungs-Link-Sichtbarkeit als useMemo
  // mit stabilen Deps. Vorher wurden showInternalDocs und showWertungLink bei
  // jedem Footer-Render neu berechnet — bei mehreren parallelen Re-Renders
  // (nach Login: Session-Update + site-state-fetch + theme-change) ergab das
  // 2-3 verschiedene Boolean-Snapshots in Folge, die wiederum die conditional
  // gerenderten Link-Items unterschiedlich erscheinen ließen → potentieller
  // React-#300-Trigger.
  const sessionUserId = session?.user?.id ?? null
  const isAdminUser = (session?.user as any)?.role === 'ADMIN'
  const showInternalDocs = useMemo(() => !!sessionUserId, [sessionUserId])
  const showWertungLink = useMemo(() => {
    if (fglWertungVisibility === 'public') return true
    if (fglWertungVisibility === 'tier_gated' && !!sessionUserId) return true
    if (isAdminUser) return true
    if (fglWertungActive) return true  // Backward-compat
    return false
  }, [fglWertungVisibility, sessionUserId, isAdminUser, fglWertungActive])

  useEffect(() => {
    fetch('/api/public/site-state')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.fglWertungActive) setFglWertungActive(true)
        const v = d?.fglWertungVisibility
        if (v === 'public' || v === 'tier_gated' || v === 'hidden') setFglWertungVisibility(v)
      })
      .catch(() => {})
  }, [])

  return (
    <footer className="fgl-footer">
      {/* 2026-05-20: drastisch verkürzte Mobile-Paddings (py-8 statt py-16). */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 lg:py-20">

        {/* Brand-Block — auf Mobile kompakt (kleinerer Headline + kleinere Tagline) */}
        <div className="grid lg:grid-cols-[2fr_1fr_1fr_1fr] gap-6 lg:gap-12 mb-6 lg:mb-14">
          <div className="lg:mb-0 mb-2">
            <div className="award-eyebrow mb-2 lg:mb-3">FREE GLIDE LEAGUE</div>
            <h4
              className="font-bold leading-tight mb-3 lg:mb-4 hidden lg:block"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(28px, 3vw, 40px)',
                letterSpacing: '-0.02em',
                color: 'var(--fgl-footer-fg)',
              }}
            >
              {t('home.brandHeroLine1')}<br />
              <span className="award-grad-text">{t('home.brandHeroLine2')}</span><br />
              {t('home.brandHeroLine3')}
            </h4>
            <p
              className="text-[12px] lg:text-[14px] leading-relaxed lg:max-w-md"
              style={{ color: 'var(--fgl-footer-muted)' }}
            >
              {t('home.footerTagline')}
            </p>
          </div>

          <FooterColumn label={t('nav.platform')} open={!!openCols[t('nav.platform')]} onToggle={() => toggleCol(t('nav.platform'))}>
            {showWertungLink && <FooterLink href="/wertung">{t('nav.rankings')}</FooterLink>}
            <FooterLink href="/features">{t('nav.features')}</FooterLink>
            <FooterLink href="/flying-fields">{t('nav.flyingFields')}</FooterLink>
            <FooterLink href="/mitmachen">{t('nav.pricing')}</FooterLink>
            {/* R100.57 (2026-05-30): Pilotengalerie entfernt — kommt in Phase 2
                als „Öffentliche Logbücher" zurück (Vee-Glide-Stil). */}
            <FooterLink href="/faq">{t('nav.faq')}</FooterLink>
            {/* 2026-08-23 (v3.10.22): Coaching-App-Einführung — 17 Screens + Voice-Commands + QR-Kopplung */}
            <FooterLink href="/coaching-app">Coaching-App Anleitung</FooterLink>
          </FooterColumn>

          <FooterColumn label={t('nav.external')} open={!!openCols[t('nav.external')]} onToggle={() => toggleCol(t('nav.external'))}>
            <FooterLink href="/datenschutz">{t('nav.datenschutz')}</FooterLink>
            <FooterLink href="/impressum">{t('nav.impressum')}</FooterLink>
            <FooterLink href="/nutzungsbedingungen">{t('nav.terms')}</FooterLink>
            <FooterLink href="/wettbewerbsordnung">{t('nav.competitionRules')}</FooterLink>
            <FooterLink href="/protests">{t('nav.protests')}</FooterLink>
            <FooterLink href="/pilotenboerse-regeln">{t('nav.marketplaceRules')}</FooterLink>
            {showInternalDocs && <FooterLink href="/terrain-erkennung">{t('nav.terrainDetection')}</FooterLink>}
            {showInternalDocs && <FooterLink href="/flugtyp-erkennung">{t('nav.flightTypeDetection')}</FooterLink>}
            <FooterLink href="/donate">{t('nav.support')}</FooterLink>
            {/* r19.44.11 (Stephan-Spec 2026-06-26): Partner-Label umbenannt
               in „Verwendbare Logger" + pro Hersteller zusaetzlich Link auf
               die entsprechende FAQ-Antwort (mit Anker, Stand 03.06.2026).
               Hersteller-Link bleibt extern. */}
            <li className="pt-3 mt-2" style={{ borderTop: '1px solid var(--fgl-footer-border)' }}>
              <p className="award-eyebrow mb-1.5" style={{ color: 'var(--fgl-footer-muted)', opacity: 0.7 }}>
                {t('nav.partnersLabel')}
              </p>
              {/* r19.55.39 (2026-07-12, Stephan-Direktive): RC-Electronics-Shop-
                  Link entfernt nach Ankuendigung CSV-only-Wechsel. Nur noch alte
                  Logger liefern valide IGC-Daten — Details in FAQ 43. */}
              <FooterLink href="/faq#faq-43">Noch verwendbare RC-Electronics-Logger</FooterLink>
              <FooterLink href="/faq#faq-41">Verwendbare Logger — Übersicht</FooterLink>
            </li>
            <li className="mt-1">
              <ExternalLink href="https://www.sm-modellbau.de" className="fgl-footer-link block">SM Modellbau</ExternalLink>
              <FooterLink href="/faq#faq-41">SM Modellbau — SM-Logger 2 &amp; 3</FooterLink>
            </li>
          </FooterColumn>

          <FooterColumn label={t('nav.contact')} open={!!openCols[t('nav.contact')]} onToggle={() => toggleCol(t('nav.contact'))}>
            <li>
              <a href="mailto:info@free-glide-league.com" className="fgl-footer-link">
                info@free-glide-league.com
              </a>
            </li>
          </FooterColumn>
        </div>

        {/* Copyright — eine Zeile, Modellpilot.EU als _blank-Link */}
        <div
          className="pt-6 lg:pt-8 text-center"
          style={{ borderTop: '1px solid var(--fgl-footer-border)' }}
        >
          <p className="text-[11px] lg:text-[12px]" style={{ color: 'var(--fgl-footer-muted)' }}>
            FREE GLIDE LEAGUE — {t('nav.aProjectOf')}{' '}
            <a
              href="https://modellpilot.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold fgl-footer-link"
            >
              Modellpilot.EU
            </a>
            {' '}· Stephan Eich · © {new Date().getFullYear()}
          </p>
          {/* r19.55.217: EU AI Act Art. 50 Transparenz — dezenter Hinweis dass
              KI-Features on-premise laufen. Basic-Kennzeichnung, weil je Seite
              das genaue Ausmass (generated/modified/none) variiert und dort
              spezifisch gekennzeichnet ist. */}
          <div className="mt-3 flex justify-center">
            <AiLabel kind="basic" size="sm" />
          </div>
        </div>
      </div>

      <style jsx global>{`
        .fgl-footer {
          --fgl-footer-bg: var(--bg-secondary, #fafafa);
          --fgl-footer-fg: var(--text, #14202a);
          --fgl-footer-muted: var(--muted, #6b7b8f);
          --fgl-footer-border: var(--border, rgba(0,0,0,0.08));
          background: var(--fgl-footer-bg);
          border-top: 1px solid var(--fgl-footer-border);
          margin-top: 60px;
        }
        [data-award="true"] .fgl-footer {
          --fgl-footer-bg: var(--award-ink-2);
          --fgl-footer-fg: var(--award-cirrus);
          --fgl-footer-muted: var(--award-cirrus-3);
          --fgl-footer-border: var(--award-line);
          margin-top: 0;
        }
        .fgl-footer-link {
          font-family: var(--font-sans);
          font-size: 13px;
          color: var(--fgl-footer-muted);
          text-decoration: none;
          transition: color 0.2s, transform 0.2s;
          display: inline-block;
        }
        .fgl-footer-link:hover {
          color: var(--award-gold, var(--accent));
          transform: translateX(2px);
        }

        /* Mobile-Accordion: Header als tappbarer Button, Liste collapsed.
           Auf lg+ wird der Header zum reinen Label, Liste immer offen. */
        .fgl-footer-col-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 12px 0;
          background: transparent;
          border: 0;
          border-bottom: 1px solid var(--fgl-footer-border);
          cursor: pointer;
          text-align: start;
        }
        .fgl-footer-col-list {
          max-height: 0;
          overflow: hidden;
          opacity: 0;
          transition: max-height 320ms cubic-bezier(.16,1,.3,1), opacity 220ms ease, padding 240ms ease;
          padding-top: 0;
        }
        .fgl-footer-col-list.is-open {
          /* R100.100 (2026-05-31): max-height angehoben — bei 10+ Links auf
             Mobile war 800px knapp und schnitt Partner-Block ab. */
          max-height: 1400px;
          opacity: 1;
          padding-top: 12px;
          padding-bottom: 8px;
        }
        @media (min-width: 1024px) {
          .fgl-footer-col-head {
            padding: 0 0 16px;
            border-bottom: 0;
            cursor: default;
            pointer-events: none;
          }
          .fgl-footer-col-list {
            max-height: none;
            opacity: 1;
            overflow: visible;
            padding-top: 0;
            transition: none;
          }
        }
      `}</style>
    </footer>
  )
}

function FooterColumn({
  label, children, open, onToggle,
}: {
  label: string
  children: React.ReactNode
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="fgl-footer-col">
      {/* Header — auf Mobile Button, auf Desktop nur Label */}
      <button
        type="button"
        onClick={onToggle}
        className="fgl-footer-col-head"
        aria-expanded={open}
      >
        <span className="award-eyebrow" style={{ color: 'var(--fgl-footer-muted)' }}>{label}</span>
        <ChevronDown
          className="lg:hidden h-3.5 w-3.5 transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : 'none', color: 'var(--fgl-footer-muted)' }}
        />
      </button>
      <ul className={`fgl-footer-col-list space-y-2 ${open ? 'is-open' : ''}`}>{children}</ul>
    </div>
  )
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <Link href={href} className="fgl-footer-link">
        {children}
      </Link>
    </li>
  )
}
